import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";
import type { Opportunity, OpportunityStage } from "@/data/opportunityTypes";
import type { SpeedToLeadSummary } from "@/data/speedToLead";
import { toUiOpportunity } from "@/mappers/lead.mapper";
import { LeadService } from "@/services/lead.service";
import {
  EMPTY_SPEED_TO_LEAD_SUMMARY,
  PipelinePageService,
  type PageResponse,
  type PipelineLeadsPageQuery,
} from "@/services/pipeline-page.service";
import { SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";
import { toUuidOrNull } from "@/lib/uuid";

interface LeadInput {
  primaryName: string;
  primaryPhone: string;
  primaryEmail: string;
  secondaryName?: string;
  secondaryPhone?: string;
  secondaryEmail?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  projectType: string;
  leadSource?: string;
  leadSourceId?: string;
  assignedRep?: string;
  notes?: string;
}

function emptyPage(): PageResponse<Opportunity> {
  return { data: [], count: 0, error: null };
}

type StagePageState = {
  data: Opportunity[];
  count: number;
  error: unknown | null;
  loading: boolean;
  nextPage: number;
};

function emptyStageState(): StagePageState {
  return { data: [], count: 0, error: null, loading: false, nextPage: 0 };
}

function dedupeLeads(leads: Opportunity[]): Opportunity[] {
  const seen = new Set<string>();
  return leads.filter((lead) => {
    if (seen.has(lead.id)) return false;
    seen.add(lead.id);
    return true;
  });
}

function toDbLeadInput(input: LeadInput) {
  return {
    primary_name: input.primaryName,
    primary_phone: input.primaryPhone,
    primary_email: input.primaryEmail || null,
    address: input.address,
    city: input.city,
    state: input.state,
    zip: input.zip,
    project_type: input.projectType,
    lead_source: input.leadSource || null,
    lead_source_id: input.leadSourceId || null,
    provider: "manual",
    assigned_rep: toUuidOrNull(input.assignedRep),
    notes: input.notes || null,
    organization_id: "",
    secondary_name: input.secondaryName || null,
    secondary_phone: input.secondaryPhone || null,
    secondary_email: input.secondaryEmail || null,
  };
}

export function usePipelineLeads(query: { enabled?: boolean; refreshToken?: number }) {
  const [state, setState] = useState<PageResponse<Opportunity>>(emptyPage());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return;
    }
    setLoading(true);
    const next = await PipelinePageService.getLeads({ refreshToken: query.refreshToken });
    setState(next);
    setLoading(false);
  }, [query.enabled, query.refreshToken]);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void PipelinePageService.getLeads({ refreshToken: query.refreshToken }).then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.refreshToken]);

  const createLead = useCallback(async (input: LeadInput): Promise<Opportunity | null> => {
    try {
      const { data, error } = await LeadService.create(toDbLeadInput(input));
      if (error || !data) {
        console.error("Failed to create lead:", error);
        return null;
      }
      const newLead = toUiOpportunity(data);
      setState((current) => ({ ...current, data: [newLead, ...current.data], count: current.count + 1 }));
      return newLead;
    } catch (error) {
      console.error("Failed to create lead:", error);
      return null;
    }
  }, []);

  return {
    leads: state.data,
    count: state.count,
    error: state.error,
    loading,
    setLeads: (updater: SetStateAction<Opportunity[]>) => {
      setState((current) => {
        const data = typeof updater === "function" ? updater(current.data) : updater;
        return { ...current, data, count: data.length };
      });
    },
    createLead,
    refresh,
  };
}

export function usePipelineLeadsPage(query: PipelineLeadsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<Opportunity>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void PipelinePageService.getLeadsPage(query).then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.page, query.pageSize, query.search, query.rep, query.stage, query.refreshToken]);

  const createLead = useCallback(async (input: LeadInput): Promise<Opportunity | null> => {
    try {
      const { data, error } = await LeadService.create(toDbLeadInput(input));
      if (error || !data) {
        console.error("Failed to create lead:", error);
        return null;
      }
      const newLead = toUiOpportunity(data);
      setState((current) => ({ ...current, data: [newLead, ...current.data], count: current.count + 1 }));
      return newLead;
    } catch (error) {
      console.error("Failed to create lead:", error);
      return null;
    }
  }, []);

  return {
    leads: state.data,
    count: state.count,
    error: state.error,
    loading,
    setLeads: (updater: SetStateAction<Opportunity[]>) => {
      setState((current) => {
        const data = typeof updater === "function" ? updater(current.data) : updater;
        return { ...current, data, count: current.count };
      });
    },
    createLead,
  };
}

export function usePipelineLeadsKanban(query: {
  enabled?: boolean;
  stages: OpportunityStage[];
  pageSize?: number;
  search?: string;
  rep?: string;
  refreshToken?: number;
}) {
  const pageSize = Math.max(1, Number(query.pageSize) || 10);
  const stagesKey = query.stages.join("|");
  const [state, setState] = useState<Record<string, StagePageState>>({});

  useEffect(() => {
    let cancelled = false;
    const stages = query.stages;
    if (!query.enabled || stages.length === 0) {
      setState({});
      return () => {
        cancelled = true;
      };
    }

    setState(() =>
      Object.fromEntries(
        stages.map((stage) => [
          stage,
          { ...emptyStageState(), loading: true },
        ]),
      ),
    );

    void Promise.all(
      stages.map(async (stage) => {
        const page = await PipelinePageService.getLeadsPage({
          page: 0,
          pageSize,
          search: query.search,
          rep: query.rep,
          stage,
          refreshToken: query.refreshToken,
        });
        return [stage, page] as const;
      }),
    ).then((entries) => {
      if (cancelled) return;
      setState(
        Object.fromEntries(
          entries.map(([stage, page]) => [
            stage,
            {
              data: page.data,
              count: page.count,
              error: page.error,
              loading: false,
              nextPage: 1,
            },
          ]),
        ),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [query.enabled, stagesKey, pageSize, query.search, query.rep, query.refreshToken]);

  const leads = useMemo(
    () => query.stages.flatMap((stage) => state[stage]?.data ?? []),
    [query.stages, stagesKey, state],
  );

  const stageCounts = useMemo(
    () =>
      Object.fromEntries(
        query.stages.map((stage) => [stage, state[stage]?.count ?? 0]),
      ) as Record<string, number>,
    [query.stages, stagesKey, state],
  );

  const stageMeta = useMemo(
    () =>
      Object.fromEntries(
        query.stages.map((stage) => {
          const bucket = state[stage] ?? emptyStageState();
          return [
            stage,
            {
              count: bucket.count,
              loaded: bucket.data.length,
              loading: bucket.loading,
              error: bucket.error,
              hasMore: bucket.data.length < bucket.count,
            },
          ];
        }),
      ) as Record<string, { count: number; loaded: number; loading: boolean; error: unknown | null; hasMore: boolean }>,
    [query.stages, stagesKey, state],
  );

  const loadMore = useCallback(async (stage: OpportunityStage) => {
    const bucket = state[stage];
    if (!query.enabled || !bucket || bucket.loading || bucket.data.length >= bucket.count) return;

    setState((current) => ({
      ...current,
      [stage]: { ...(current[stage] ?? bucket), loading: true },
    }));

    const page = await PipelinePageService.getLeadsPage({
      page: bucket.nextPage,
      pageSize,
      search: query.search,
      rep: query.rep,
      stage,
      refreshToken: query.refreshToken,
    });

    setState((current) => {
      const currentBucket = current[stage] ?? bucket;
      return {
        ...current,
        [stage]: {
          data: dedupeLeads([...currentBucket.data, ...page.data]),
          count: page.count,
          error: page.error,
          loading: false,
          nextPage: currentBucket.nextPage + 1,
        },
      };
    });
  }, [query.enabled, state, pageSize, query.search, query.rep, query.refreshToken]);

  const createLead = useCallback(async (input: LeadInput): Promise<Opportunity | null> => {
    try {
      const { data, error } = await LeadService.create(toDbLeadInput(input));
      if (error || !data) {
        console.error("Failed to create lead:", error);
        return null;
      }
      const newLead = toUiOpportunity(data);
      setState((current) => {
        const stage = newLead.stage;
        const bucket = current[stage] ?? emptyStageState();
        return {
          ...current,
          [stage]: {
            ...bucket,
            data: dedupeLeads([newLead, ...bucket.data]),
            count: bucket.count + 1,
          },
        };
      });
      return newLead;
    } catch (error) {
      console.error("Failed to create lead:", error);
      return null;
    }
  }, []);

  return {
    leads,
    count: Object.values(stageCounts).reduce((sum, count) => sum + count, 0),
    stageCounts,
    stageMeta,
    error: Object.values(state).find((bucket) => bucket.error)?.error ?? null,
    loading: Object.values(state).some((bucket) => bucket.loading && bucket.data.length === 0),
    loadingAny: Object.values(state).some((bucket) => bucket.loading),
    setLeads: (updater: SetStateAction<Opportunity[]>) => {
      setState((current) => {
        const currentLeads = query.stages.flatMap((stage) => current[stage]?.data ?? []);
        const nextLeads = typeof updater === "function" ? updater(currentLeads) : updater;
        const byStage = new Map<string, Opportunity[]>();
        nextLeads.forEach((lead) => {
          const stage = query.stages.includes(lead.stage) ? lead.stage : query.stages[0];
          if (!stage) return;
          byStage.set(stage, [...(byStage.get(stage) ?? []), lead]);
        });

        return Object.fromEntries(
          query.stages.map((stage) => {
            const bucket = current[stage] ?? emptyStageState();
            const data = byStage.get(stage) ?? [];
            const delta = data.length - bucket.data.length;
            return [
              stage,
              {
                ...bucket,
                data,
                count: Math.max(data.length, bucket.count + delta),
              },
            ];
          }),
        );
      });
    },
    createLead,
    loadMore,
  };
}

export function usePipelineSpeedToLeadSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [summary, setSummary] = useState<SpeedToLeadSummary>(EMPTY_SPEED_TO_LEAD_SUMMARY);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setSummary(EMPTY_SPEED_TO_LEAD_SUMMARY);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    void PipelinePageService.getSpeedToLeadSummary({ refreshToken: query.refreshToken }).then((next) => {
      if (cancelled) return;
      setSummary(next.data);
      setError(next.error);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [query.enabled, query.refreshToken]);

  return { summary, error, loading };
}

export function usePipelineRealtimeRefreshSignal(enabled: boolean) {
  const [refreshToken, setRefreshToken] = useState(0);
  const channelNameRef = useRef(`pipeline-server-page-sync-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!enabled) return;
    const bump = (table: string, payload: { eventType?: string; new?: { id?: string | null }; old?: { id?: string | null } }) => {
      const entityId = String(payload.new?.id ?? payload.old?.id ?? "");
      recordRealtimeMetric({
        source: "pipeline-server-page",
        event: "exact-refetch",
        table,
        entityId: entityId || undefined,
        detail: SUPABASE_SYNC_DEBUG ? { eventType: payload.eventType } : undefined,
      });
      setRefreshToken((current) => current + 1);
    };

    const channel = supabase
      .channel(channelNameRef.current)
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, (payload) => bump("leads", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "quotes" }, (payload) => bump("quotes", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "appointment_results" }, (payload) => bump("appointment_results", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "conversations" }, (payload) => bump("conversations", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, (payload) => bump("messages", payload as any))
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled]);

  return refreshToken;
}
